/**
 * Reportes → Reporte de gastos (`/reportes/gastos`).
 *
 * Puerto de `Reportes::reporte_gastos_periodo` + `Reportes_model::get_gastos_by_periodo`
 * + `views/menu/reportes/reporte_gastos_periodo.php` (+ `src/reports/daily_expenses.js`).
 *
 * La pantalla del panel anterior pedía **dos fechas**, pintaba la tabla de gastos
 * (ID, fecha, monto, descripción, turno y grifero) con el total en el pie y
 * ofrecía Excel y PDF.
 *
 * Reglas conservadas:
 * - Se listan los gastos **no anulados** (`gastos.estado <> 2`) del rango, con su
 *   turno y su trabajador, y el pie suma el importe.
 * - El importe es `gastos.cantidad` (la columna «MONTO» del original).
 *
 * Diferencias anotadas:
 * - El rango de fechas se valida y trae **30 días** por defecto; en el original,
 *   sin fechas, la consulta listaba todo el histórico.
 * - Se puede filtrar por **tipo** (operativos o administrativos) y por
 *   **trabajador**, con el atajo de los meses con gastos.
 * - El **trabajador** es el del turno y se añade quién lo registró: en el
 *   original la columna «GRIFERO» salía de `gastos.usuario_id`, así que mostraba
 *   al que cargó el gasto (a veces un administrador, no el grifero). Cuando el
 *   turno del gasto ya no existe se usa al que lo registró.
 * - Se añade el reparto por **día** y por **trabajador** y el CSV lo sirve la API.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatHoraValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosGastos,
  useGastosPeriodo,
  type FiltrosGastos,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto del reporte: los últimos 30 días. */
function rangoPorDefecto(): FiltrosGastos {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getTime() - 30 * 24 * 60 * 60 * 1000)),
    hasta: fechaLocal(hoy),
    tipo: '',
    usuario: '',
  };
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string | null | undefined): string {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return periodo ?? '';

  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

/** Primer y último día de un mes `AAAA-MM`. */
function mesComoRango(periodo: string): { desde: string; hasta: string } {
  const anio = Number(periodo.slice(0, 4));
  const mes = Number(periodo.slice(5, 7));

  return {
    desde: `${periodo}-01`,
    hasta: fechaLocal(new Date(anio, mes, 0)),
  };
}

export function ReporteGastosPage() {
  useDocumentTitle('Reportes · Reporte de gastos');

  const [filtros, setFiltros] = useState<FiltrosGastos>(rangoPorDefecto);
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosGastos();
  const consulta = useGastosPeriodo(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const meses = catalogos.data?.meses ?? [];

  const gastos = useMemo(() => datos?.gastos ?? [], [datos]);
  // El listado se pagina en el navegador; los repartos y el pie siguen usando el listado
  // completo.
  const paginacion = usePaginacion(gastos, { clave: filtros });

  const cambiar = <C extends keyof FiltrosGastos>(campo: C, valor: FiltrosGastos[C]) =>
    setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarGastos(filtros), {
        nombre: `gastos-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfGastos(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Reporte de gastos</h1>
          <p className="mt-1 text-sm text-ink-500">
            Gastos del periodo con su turno, su trabajador y su tipo.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Tipo</span>
              <Select
                value={filtros.tipo}
                onChange={({ target }) => cambiar('tipo', target.value)}
                className={CONTROL}
                aria-label="Tipo de gasto"
              >
                {(catalogos.data?.tipos ?? []).map((tipo) => (
                  <option key={tipo.valor} value={tipo.valor}>
                    {tipo.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Trabajador</span>
              <Select
                value={filtros.usuario}
                onChange={({ target }) => cambiar('usuario', target.value)}
                className={CONTROL}
                aria-label="Trabajador"
              >
                <option value="">TODOS</option>
                {(catalogos.data?.trabajadores ?? []).map((trabajador) => (
                  <option key={trabajador.id} value={String(trabajador.id)}>
                    {trabajador.nombre} ({formatNumber(trabajador.movimientos)})
                  </option>
                ))}
              </Select>
            </label>
          </div>

          {meses.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                <Search className="size-3.5" aria-hidden="true" />
                Meses con gastos
              </span>
              {meses.slice(0, 12).map((mes) => {
                const rango = mesComoRango(mes.periodo);
                const activo = rango.desde === filtros.desde && rango.hasta === filtros.hasta;

                return (
                  <button
                    key={mes.periodo}
                    type="button"
                    aria-pressed={activo}
                    onClick={() => setFiltros((previos) => ({ ...previos, ...rango }))}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                      activo
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                    )}
                    title={`${formatNumber(mes.movimientos)} gasto(s)`}
                  >
                    {etiquetaMes(mes.periodo)}
                  </button>
                );
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {[
          {
            etiqueta: 'Gastos',
            valor: formatNumber(resumen?.gastos ?? 0),
            detalle: `${formatNumber(resumen?.dias ?? 0)} día(s) con gastos`,
          },
          {
            etiqueta: 'Total gastado',
            valor: formatCurrency(resumen?.total ?? 0),
            detalle: `promedio ${formatCurrency(resumen?.promedio ?? 0)}`,
          },
          {
            etiqueta: 'Operativos',
            valor: formatCurrency(resumen?.operativo ?? 0),
            detalle: 'tipo_gasto 1',
          },
          {
            etiqueta: 'Administrativos',
            valor: formatCurrency(resumen?.administrativo ?? 0),
            detalle: 'tipo_gasto 2',
          },
          {
            etiqueta: 'Mayor gasto',
            valor: formatCurrency(resumen?.mayor ?? 0),
            detalle: `${formatNumber(resumen?.trabajadores ?? 0)} trabajador(es)`,
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por trabajador</h2>

            <div className="max-h-72 overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Trabajador</th>
                    <th className="w-[5.5rem] px-2 py-2 text-right">Gastos</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(datos?.por_trabajador ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-2 py-6 text-center text-sm text-ink-500">
                        Sin gastos en el periodo.
                      </td>
                    </tr>
                  ) : null}

                  {(datos?.por_trabajador ?? []).map((fila) => (
                    <tr key={fila.trabajador} className="border-t border-ink-100">
                      <td className="px-2 py-2 text-ink-800">{fila.trabajador}</td>
                      <td className="px-2 py-2 text-right text-ink-600">
                        {formatNumber(fila.movimientos)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por día</h2>

            <div className="max-h-72 overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Fecha</th>
                    <th className="w-[5.5rem] px-2 py-2 text-right">Gastos</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(datos?.por_dia ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-2 py-6 text-center text-sm text-ink-500">
                        Sin gastos en el periodo.
                      </td>
                    </tr>
                  ) : null}

                  {(datos?.por_dia ?? []).map((fila) => (
                    <tr key={fila.fecha} className="border-t border-ink-100">
                      <td className="px-2 py-2 text-ink-800">{formatDateValue(fila.fecha)}</td>
                      <td className="px-2 py-2 text-right text-ink-600">
                        {formatNumber(fila.movimientos)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Gastos del periodo ({formatNumber(resumen?.gastos ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {formatDateValue(filtros.desde)} — {formatDateValue(filtros.hasta)}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[3.5rem] px-1.5 py-2">ID</th>
                    <th className="w-[5.25rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[5.5rem] px-1.5 py-2 text-right">Monto</th>
                    <th className="px-1.5 py-2">Descripción</th>
                    <th className="w-[6rem] px-1.5 py-2">Trabajador</th>
                    <th className="w-[4rem] px-1.5 py-2">Turno</th>
                  </tr>
                </thead>
                <tbody>
                  {gastos.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={6} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay gastos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila) => (
                    <tr key={fila.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="px-1.5 py-2 text-ink-600">{fila.id}</td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900">
                          {formatDateValue(fila.fecha)}
                        </p>
                        {fila.hora_llegada ? (
                          <p
                            className="truncate text-[11px] text-ink-500"
                            title={`Llegada ${fila.hora_llegada} · Salida ${fila.hora_salida ?? '—'}`}
                          >
                            {formatHoraValue(fila.hora_llegada)}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.total ?? 0)}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.descripcion ?? ''}>
                          {fila.descripcion || '—'}
                        </p>
                        <Badge variant="neutral" className="mt-0.5 px-1.5 py-0 text-[10px]">
                          {fila.tipo}
                        </Badge>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.trabajador ?? ''}>
                          {fila.trabajador ?? '—'}
                        </p>
                        {fila.registrado_por && fila.registrado_por !== fila.trabajador ? (
                          <p
                            className="truncate text-[11px] text-ink-500"
                            title={`Registrado por ${fila.registrado_por}`}
                          >
                            reg. {fila.registrado_por}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2 text-ink-600">
                        <p className="truncate" title={`Turno ${fila.turno_id ?? '—'}`}>
                          {fila.turno_id ?? '—'}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {gastos.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={2}>
                        SUMA · {formatNumber(resumen?.gastos ?? 0)} gasto(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.total ?? 0)}
                      </td>
                      <td className="px-1.5 py-2" colSpan={3}>
                        <span className="text-[11px] font-normal text-ink-500">
                          {formatNumber(resumen?.dias ?? 0)} día(s) ·{' '}
                          {formatNumber(resumen?.trabajadores ?? 0)} trabajador(es)
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="gastos" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Se listan los gastos no anulados del rango (el importe es la columna «cantidad» de la base)
        con el total en el pie. El trabajador es el del turno y, si el turno ya no existe, el que
        registró el gasto; cuando son distintos se muestran los dos. El mismo listado se descarga en
        CSV y en PDF.
      </p>
    </div>
  );
}
